import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gift, Heart, Search, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import PublicShell, { WIDE } from "@/components/PublicShell";
import { READINGS } from "@/lib/readingsContent";
import { usePromo, PromoCountdown } from "@/lib/promo";
import { isNative } from "@/lib/native";
import ShareOfferTab from "@/components/ShareOfferTab";

const HEADER_IMG = "https://images.unsplash.com/photo-1528818955841-a7f1425131b5?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

function PromoCard({ r, promo }) {
  return (
    <Link to={`/readings/${r.slug}`} data-testid={`reading-card-${r.slug}`}
      className={`group block bg-gradient-to-br from-[#1a1230] to-[#0F1523] border border-[#D4AF37]/50 rounded-3xl p-6 hover:border-[#D4AF37] transition-colors ${WIDE ? "lg:col-span-3 lg:p-10 lg:flex lg:items-center lg:gap-10" : ""}`}>
      <div className="flex-1">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] bg-[#D4AF37] text-[#04060B] px-3 py-1 rounded-full mb-3">
          <Sparkles size={11} /> Limited-time offer{promo.extended ? ", extended" : ""}
        </span>
        <h2 className={`text-3xl font-light ${WIDE ? "lg:text-4xl" : ""}`}><Heart size={20} className="inline gold-text -mt-1 mr-2" />{r.title}</h2>
        <p className={`text-base text-[#D6D0C5] mt-2 mb-3 ${WIDE ? "lg:text-lg lg:max-w-xl" : ""}`}>{r.short} Free when you join Astra Premium.</p>
        <PromoCountdown endsAt={promo.ends_at} />
      </div>
      <span className="mt-4 lg:mt-0 inline-flex items-center gap-2 gold-text text-base group-hover:gap-3 transition-[gap]">Claim it free <ArrowRight size={16} /></span>
    </Link>
  );
}

function Card({ r }) {
  return (
    <Link to={`/readings/${r.slug}`} data-testid={`reading-card-${r.slug}`}
      className="group flex flex-col bg-[#0F1523]/90 border border-[#D4AF37]/15 hover:border-[#D4AF37]/50 rounded-2xl p-5 transition-colors">
      <span className="text-[10px] uppercase tracking-[0.2em] gold-text">{r.tag}</span>
      <h3 className="text-xl font-medium mt-0.5">{r.title}</h3>
      <p className="text-sm text-[#A7B0C0] mt-1 flex-1">{r.short}</p>
      <span className="mt-3 inline-flex items-center gap-1.5 text-sm gold-text group-hover:gap-2.5 transition-[gap]">What's included <ArrowRight size={14} /></span>
    </Link>
  );
}

function GiftCard() {
  return (
    <Link to="/gifts" data-testid="readings-gift-card"
      className="group flex flex-col bg-gradient-to-br from-[#2a2110] to-[#0F1523] border border-[#D4AF37]/40 hover:border-[#D4AF37] rounded-2xl p-5 transition-colors">
      <span className="text-[10px] uppercase tracking-[0.2em] gold-text flex items-center gap-1.5"><Gift size={12} /> For a friend</span>
      <h3 className="text-xl font-medium mt-0.5">Gift a reading</h3>
      <p className="text-sm text-[#A7B0C0] mt-1 flex-1">Choose a reading and the day it arrives in their inbox: a birthday, an anniversary, or just because.</p>
      <span className="mt-3 inline-flex items-center gap-1.5 text-sm gold-text group-hover:gap-2.5 transition-[gap]">Send a gift <ArrowRight size={14} /></span>
    </Link>
  );
}

function SearchBox({ q, setQ }) {
  return (
    <div className={`px-5 pt-5 ${WIDE ? "lg:max-w-6xl lg:mx-auto lg:px-10 lg:pt-8" : ""}`}>
      <label className="relative block lg:max-w-md">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 gold-text pointer-events-none" />
        <input data-testid="readings-search" type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search readings: love, career, birthday…" aria-label="Search readings"
          className="w-full bg-[#0F1523]/90 border border-[#D4AF37]/25 focus:border-[#D4AF37]/60 rounded-full pl-11 pr-4 py-3 text-base text-starlight placeholder:text-[#5f6b7d] focus:outline-none transition-colors" />
      </label>
    </div>
  );
}

const SYNONYMS = { love: ["relationship", "partner", "romance", "compatib", "venus"], career: ["work", "job", "money", "purpose"], birthday: ["solar return", "year ahead"], travel: ["move", "location", "astrocartography"] };
const matches = (r, q) => {
  const text = [r.title, r.tag, r.short, r.intro, ...(r.includes || [])].join(" ").toLowerCase();
  return [q, ...(SYNONYMS[q] || [])].some((t) => text.includes(t));
};

export default function ReadingsIndex() {
  const promo = usePromo();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const slug = !isNative && promo ? promo.slug : null;
  const term = q.trim().toLowerCase();
  const shown = term ? READINGS.filter((r) => matches(r, term)) : READINGS;
  const featured = shown.find((r) => r.slug === slug);
  const rest = shown.filter((r) => r !== featured);
  const showGift = user && !user.is_demo && !isNative && !term;
  return (
    <PublicShell testId="readings-index">
      {featured && <ShareOfferTab slug={featured.slug} />}
      <div className={`relative h-64 -mt-16 overflow-hidden pointer-events-none ${WIDE ? "lg:h-72 lg:-mt-20" : ""}`}>
        <img src={HEADER_IMG} alt="" className="w-full h-full object-cover opacity-50"
          style={{ maskImage: "linear-gradient(to bottom, #000 40%, transparent)", WebkitMaskImage: "linear-gradient(to bottom, #000 40%, transparent)" }} />
        <div className={`absolute inset-0 flex flex-col justify-end px-5 pb-3 ${WIDE ? "lg:max-w-6xl lg:mx-auto lg:px-10 lg:pb-8" : ""}`}>
          <p className="text-[11px] uppercase tracking-[0.3em] gold-text">In-depth readings</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light">Sacred Western Astrology</h1>
          <p className={`text-sm text-[#A7B0C0] mt-1 ${WIDE ? "lg:text-lg" : ""}`}>Each reading is written from the real positions of the planets on your birthday.</p>
        </div>
      </div>
      <SearchBox q={q} setQ={setQ} />
      <div data-testid="readings-list" className={`px-5 pt-4 space-y-3 ${WIDE ? "lg:max-w-6xl lg:mx-auto lg:px-10 lg:pt-5 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-5" : ""}`}>
        {featured && <PromoCard r={featured} promo={promo} />}
        {rest.map((r) => <Card key={r.slug} r={r} />)}
        {showGift && <GiftCard />}
      </div>
      {term && !shown.length && (
        <p data-testid="readings-search-empty" className="px-5 py-10 text-center text-[#A7B0C0]">
          No readings match "{q.trim()}". <button onClick={() => setQ("")} className="gold-text underline underline-offset-4" data-testid="readings-search-clear">Show all readings</button>
        </p>
      )}
    </PublicShell>
  );
}
